import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {firstValueFrom} from 'rxjs';
import {
  NzI18nInterface, NzI18nService,
  ar_EG, cs_CZ, de_DE, el_GR, en_US, es_ES, fr_FR, hr_HR, hu_HU, hy_AM, it_IT,
  ja_JP, kmr_IQ, pl_PL, pt_PT, ro_RO, ru_RU, sr_RS, tr_TR, uk_UA, zh_CN
} from 'ng-zorro-antd/i18n';
import moment from 'moment';
import {
  MbscLocale, setOptions as setMobiscrollOptions,
  localeAr, localeCs, localeDe, localeEl, localeEn, localeEs, localeFr, localeHr, localeHu, localeIt,
  localeJa, localePl, localePtPT, localeRo, localeRu, localeSr, localeTr, localeUa, localeZh
} from '@mobiscroll/angular';

export type WebLanguage =
  'de' | 'en' | 'es' | 'it' | 'pl' | 'sr' | 'tr' | 'uk' | 'ru' | 'ro' | 'hu' |
  'el' | 'hr' | 'fr' | 'cs' | 'ar' | 'bs' | 'zh' | 'ja' | 'ku' | 'pt' | 'hy' | 'sq';

// Kalender (Mobiscroll) – ohne eigene Sprache: Bosnisch wie Kroatisch, sonst Englisch
const MOBISCROLL_LOCALES: Record<WebLanguage, MbscLocale> = {
  de: localeDe, en: localeEn, es: localeEs, it: localeIt, pl: localePl, sr: localeSr, tr: localeTr, uk: localeUa,
  ru: localeRu, ro: localeRo, hu: localeHu, el: localeEl, hr: localeHr, fr: localeFr, cs: localeCs, ar: localeAr,
  bs: localeHr, zh: localeZh, ja: localeJa, ku: localeEn, pt: localePtPT, hy: localeEn, sq: localeEn
};

interface LanguageConfig {
  label: string;
  nzLocale: NzI18nInterface;
  // Moment-Locale wird erst bei Bedarf nachgeladen; 'en' ist in moment eingebaut, 'de' wird in app.module geladen
  momentLocale: string;
  loadMomentLocale?: () => Promise<unknown>;
}

/**
 * Zentrale Stelle für die Sprache der Weboberfläche.
 * Die Übersetzungen liegen in Shared/i18n und werden mit der App geteilt.
 */
@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  public static readonly DEFAULT_LANGUAGE: WebLanguage = 'de';
  // Gleicher Key wie in der App (settings.service.ts)
  private static readonly STORAGE_KEY = 'settings.language';

  public readonly languages: Record<WebLanguage, LanguageConfig> = {
    de: {label: 'Deutsch (German)', nzLocale: de_DE, momentLocale: 'de'},
    en: {label: 'English (English)', nzLocale: en_US, momentLocale: 'en'},
    es: {label: 'Español (Spanish)', nzLocale: es_ES, momentLocale: 'es', loadMomentLocale: () => import('moment/locale/es')},
    it: {label: 'Italiano (Italian)', nzLocale: it_IT, momentLocale: 'it', loadMomentLocale: () => import('moment/locale/it')},
    pl: {label: 'Polski (Polish)', nzLocale: pl_PL, momentLocale: 'pl', loadMomentLocale: () => import('moment/locale/pl')},
    // sr_RS ist in ng-zorro unvollständig, fehlende Texte kommen aus en_US
    sr: {label: 'Srpski (Serbian)', nzLocale: {...en_US, ...sr_RS, Pagination: {...en_US.Pagination, ...sr_RS.Pagination}}, momentLocale: 'sr', loadMomentLocale: () => import('moment/locale/sr')},
    tr: {label: 'Türkçe (Turkish)', nzLocale: tr_TR, momentLocale: 'tr', loadMomentLocale: () => import('moment/locale/tr')},
    uk: {label: 'Ukraïnska (Ukrainian)', nzLocale: uk_UA, momentLocale: 'uk', loadMomentLocale: () => import('moment/locale/uk')},
    ru: {label: 'Русский (Russian)', nzLocale: ru_RU, momentLocale: 'ru', loadMomentLocale: () => import('moment/locale/ru')},
    ro: {label: 'Română (Romanian)', nzLocale: ro_RO, momentLocale: 'ro', loadMomentLocale: () => import('moment/locale/ro')},
    hu: {label: 'Magyar (Hungarian)', nzLocale: hu_HU, momentLocale: 'hu', loadMomentLocale: () => import('moment/locale/hu')},
    el: {label: 'Ελληνικά (Greek)', nzLocale: el_GR, momentLocale: 'el', loadMomentLocale: () => import('moment/locale/el')},
    hr: {label: 'Hrvatski (Croatian)', nzLocale: hr_HR, momentLocale: 'hr', loadMomentLocale: () => import('moment/locale/hr')},
    fr: {label: 'Français (French)', nzLocale: fr_FR, momentLocale: 'fr', loadMomentLocale: () => import('moment/locale/fr')},
    cs: {label: 'Čeština (Czech)', nzLocale: cs_CZ, momentLocale: 'cs', loadMomentLocale: () => import('moment/locale/cs')},
    ar: {label: 'العربية (Arabic)', nzLocale: ar_EG, momentLocale: 'ar', loadMomentLocale: () => import('moment/locale/ar')},
    // ng-zorro hat kein Bosnisch, Kroatisch ist am nächsten
    bs: {label: 'Bosanski (Bosnian)', nzLocale: hr_HR, momentLocale: 'bs', loadMomentLocale: () => import('moment/locale/bs')},
    zh: {label: '中文 (Chinese)', nzLocale: zh_CN, momentLocale: 'zh-cn', loadMomentLocale: () => import('moment/locale/zh-cn')},
    ja: {label: '日本語 (Japanese)', nzLocale: ja_JP, momentLocale: 'ja', loadMomentLocale: () => import('moment/locale/ja')},
    ku: {label: 'Kurdî (Kurdish)', nzLocale: kmr_IQ, momentLocale: 'ku-kmr', loadMomentLocale: () => import('moment/locale/ku-kmr')},
    pt: {label: 'Português (Portuguese)', nzLocale: pt_PT, momentLocale: 'pt', loadMomentLocale: () => import('moment/locale/pt')},
    hy: {label: 'Հայերեն (Armenian)', nzLocale: hy_AM, momentLocale: 'hy-am', loadMomentLocale: () => import('moment/locale/hy-am')},
    // ng-zorro hat kein Albanisch, fehlende Texte kommen aus en_US
    sq: {label: 'Shqip (Albanian)', nzLocale: en_US, momentLocale: 'sq', loadMomentLocale: () => import('moment/locale/sq')},
  };

  constructor(
    private translateService: TranslateService,
    private nzI18nService: NzI18nService,
  ) {
  }

  get currentLanguage(): WebLanguage {
    return (this.translateService.getCurrentLang() as WebLanguage) || LanguageService.DEFAULT_LANGUAGE;
  }

  /**
   * Wird beim App-Start aufgerufen. Ohne gespeicherte Auswahl bleibt die Oberfläche auf Deutsch,
   * damit nicht plötzlich teilweise übersetzte Seiten erscheinen.
   */
  public init(): Promise<void> {
    return this.setLanguage(this.getStoredLanguage(), false);
  }

  public async setLanguage(language: WebLanguage, persist = true): Promise<void> {
    const config = this.languages[language] ?? this.languages[LanguageService.DEFAULT_LANGUAGE];
    const lang = this.languages[language] ? language : LanguageService.DEFAULT_LANGUAGE;

    if (persist) {
      try {
        localStorage.setItem(LanguageService.STORAGE_KEY, lang);
      } catch (e) {
        // localStorage nicht verfügbar – Auswahl gilt dann nur für diese Sitzung
      }
    }

    try {
      await config.loadMomentLocale?.();
    } catch (e) {
      console.warn('Moment-Locale konnte nicht geladen werden', lang, e);
    }
    moment.locale(config.momentLocale);
    this.nzI18nService.setLocale(config.nzLocale);
    setMobiscrollOptions({locale: MOBISCROLL_LOCALES[lang]});
    document.documentElement.lang = lang;

    try {
      await firstValueFrom(this.translateService.use(lang));
    } catch (e) {
      // Fehlende Sprachdatei: Keys sind deutsche Texte, die Oberfläche bleibt also lesbar
      console.warn('Übersetzungen konnten nicht geladen werden', lang, e);
    }
  }

  private getStoredLanguage(): WebLanguage {
    try {
      const stored = localStorage.getItem(LanguageService.STORAGE_KEY) as WebLanguage;
      if (stored && this.languages[stored]) {
        return stored;
      }
    } catch (e) {
      // localStorage nicht verfügbar
    }
    return LanguageService.DEFAULT_LANGUAGE;
  }
}
